/**
 * Rename Store
 *
 * Zustand store for managing inline rename state for workspace items.
 * Handles rename mode, temporary values, and F2 keyboard shortcut.
 *
 * @module features/workspace/stores/useRenameStore
 */

import { create } from 'zustand';
import type { WorkspaceItemType } from '@loader/shared-types';

// ============================================================================
// TYPES
// ============================================================================

/**
 * State for inline renaming
 */
interface RenameState {
  /** ID of the item currently being renamed */
  renamingItemId: string | null;
  /** Type of the item being renamed */
  renamingItemType: WorkspaceItemType | null;
  /** Temporary value while editing */
  renameValue: string;
}

/**
 * Actions available on the rename store
 */
interface RenameActions {
  /** Start renaming an item */
  startRename: (id: string, type: WorkspaceItemType, initialValue: string) => void;
  /** Update the rename value */
  setRenameValue: (value: string) => void;
  /** Cancel renaming and clear state */
  cancelRename: () => void;
  /** Check if a specific item is being renamed */
  isRenaming: (id: string) => boolean;
}

/**
 * Combined store type
 */
type RenameStore = RenameState & RenameActions;

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: RenameState = {
  renamingItemId: null,
  renamingItemType: null,
  renameValue: '',
};

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

/**
 * Rename store
 *
 * Manages state for inline renaming of workspace items.
 * Supports tables, linked tables, and folders.
 */
export const useRenameStore = create<RenameStore>((set, get) => ({
  ...initialState,

  startRename: (id, type, initialValue) => {
    set({
      renamingItemId: id,
      renamingItemType: type,
      renameValue: initialValue,
    });
  },

  setRenameValue: (value) => {
    set({ renameValue: value });
  },

  cancelRename: () => {
    set(initialState);
  },

  isRenaming: (id) => {
    return get().renamingItemId === id;
  },
}));
